import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Producer = Tables<"producers">;

export type UseProducerResult = {
  producer: Producer | null;
  enabledFeatures: string[];
  loading: boolean;
  error?: string;
  isPreview?: boolean;
};

/**
 * Hook for fetching a single producer by slug or ID, including enabled features.
 * Supports ?preview=true for owners to view their draft farms.
 */
export function useProducer(slugOrId: string): UseProducerResult {
  const [searchParams] = useSearchParams();
  const previewParam = searchParams.get("preview");
  const isPreview = previewParam === "true" || previewParam === "1";
  
  const [producer, setProducer] = useState<Producer | null>(null);
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!slugOrId) {
      setLoading(false);
      setProducer(null);
      return;
    }

    let cancelled = false;

    async function fetchProducer() {
      setLoading(true);
      setError(undefined);

      try {
        // Get current user for preview authorization
        const { data: { user } } = await supabase.auth.getUser();
        
        // Build query - in preview mode, we don't filter by status
        // RLS will handle authorization (only members can see draft producers)
        let query = supabase.from("producers").select("*");
        
        // Try by slug first
        let { data, error: queryError } = await query
          .eq("slug", slugOrId)
          .maybeSingle();

        // If not found by slug, try by ID — only if it looks like a UUID
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);
        if (!data && !queryError && isUuid) {
          const result = await supabase
            .from("producers")
            .select("*")
            .eq("id", slugOrId)
            .maybeSingle();
          data = result.data;
          queryError = result.error;
        }

        if (!cancelled) {
          if (queryError) {
            console.error("Error fetching producer:", queryError);
            setError("Kunde inte hämta värd.");
            setProducer(null);
          } else if (data) {
            // Check access: public status OR user is a member
            const isPublic = data.status === "public";
            
            if (!isPublic) {
              // Non-public producer: verify user is a member (covers preview + portal use)
              if (user) {
                const { data: membership } = await supabase
                  .rpc("is_producer_member", { p_producer_id: data.id });
                
                if (!membership && !isPreview) {
                  // Not a member and not preview mode = 404
                  setProducer(null);
                  setLoading(false);
                  return;
                } else if (!membership && isPreview) {
                  setError("Du har inte behörighet att förhandsgranska denna gård.");
                  setProducer(null);
                  setLoading(false);
                  return;
                }
              } else {
                // Not logged in, can't access draft
                if (isPreview) {
                  setError("Du måste vara inloggad för att förhandsgranska.");
                }
                setProducer(null);
                setLoading(false);
                return;
              }
            }
            
            // Set producer data
            setProducer(data);

            // Fetch effective features (plan + admin overrides) via RPC – SoT
            if (data?.id) {
              const { data: effectiveData } = await supabase
                .rpc("get_producer_effective_features", { p_producer_id: data.id });

              if (!cancelled && effectiveData) {
                setEnabledFeatures(
                  effectiveData
                    .filter((f: any) => f.effective_enabled === true)
                    .map((f: any) => f.feature_key)
                );
              }
            }
          } else {
            setProducer(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Exception fetching producer:", err);
          setError("Ett oväntat fel uppstod.");
          setProducer(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchProducer();

    return () => {
      cancelled = true;
    };
  }, [slugOrId, isPreview]);

  return { producer, enabledFeatures, loading, error, isPreview };
}
